import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import { catPrefix } from '../../utils/tsc';
import './TscHeatmap.css';

function coverageColor(pct) {
  if (pct >= 80) return { bg: '#f0fdf4', border: '#86efac', text: '#166534', bar: '#16a34a' };
  if (pct >= 40) return { bg: '#fffbeb', border: '#fde68a', text: '#92400e', bar: '#d97706' };
  return { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', bar: '#dc2626' };
}


export default function TscHeatmap() {
  const { t } = useTranslation();
  const PREFIX_LABELS = {
    CC: t('TscHeatmap.prefixCommonCriteria'),
    A1: t('TscHeatmap.prefixAvailability'),
    C1: t('TscHeatmap.prefixConfidentiality'),
    PI: t('TscHeatmap.prefixProcessingIntegrity'),
    P:  t('TscHeatmap.prefixPrivacy'),
  };
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    axiosInstance.get('/audit/hub')
      .then(r => setCategories(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="hm-panel">
      <div className="hm-header">
        <span className="hm-title">{t('TscHeatmap.title')}</span>
      </div>
      <div className="hm-loading">{t('TscHeatmap.loading')}</div>
    </div>
  );

  if (categories.length === 0) return null;

  // Group by prefix
  const grouped = {};
  for (const cat of categories) {
    const prefix = catPrefix(cat.shorthand);
    if (!grouped[prefix]) grouped[prefix] = [];
    grouped[prefix].push(cat);
  }

  // Overall coverage
  const totalTests  = categories.reduce((s, c) => s + c.total,  0);
  const totalPassed = categories.reduce((s, c) => s + c.passed, 0);
  const overall     = totalTests > 0 ? Math.round((totalPassed / totalTests) * 100) : 0;
  const overallColor = coverageColor(overall);

  return (
    <div className="hm-panel">
      <div className="hm-header">
        <span className="hm-title">{t('TscHeatmap.heatmapTitle')}</span>
        <div className="hm-legend">
          <span className="hm-leg-item hm-leg-green">{t('TscHeatmap.legendGood')}</span>
          <span className="hm-leg-item hm-leg-amber">{t('TscHeatmap.legendPartial')}</span>
          <span className="hm-leg-item hm-leg-red">{t('TscHeatmap.legendGap')}</span>
        </div>
      </div>

      <div className="hm-overall" style={{ background: overallColor.bg, borderColor: overallColor.border }}>
        <div className="hm-overall-score" style={{ color: overallColor.text }}>{overall}%</div>
        <div className="hm-overall-label">{t('TscHeatmap.overallCoverage')}</div>
        <div className="hm-overall-bar-wrap">
          <div className="hm-overall-bar" style={{ width: `${overall}%`, background: overallColor.bar }} />
        </div>
      </div>

      <div className="hm-grid">
        {Object.entries(grouped).map(([prefix, cats]) => (
          <div key={prefix} className="hm-group">
            <div className="hm-group-label">{PREFIX_LABELS[prefix] || prefix}</div>
            <div className="hm-tiles">
              {cats.map(cat => {
                const c = coverageColor(cat.coverage);
                return (
                  <div key={cat.shorthand} className={`hm-tile ${cat.inScope === false ? 'hm-oos' : ''}`}
                       style={{ background: c.bg, borderColor: c.border }}>
                    <div className="hm-tile-code" style={{ color: c.text }}>{cat.shorthand}</div>
                    <div className="hm-tile-pct"  style={{ color: c.text }}>{cat.coverage}%</div>
                    <div className="hm-tile-bar-wrap">
                      <div className="hm-tile-bar" style={{ width: `${cat.coverage}%`, background: c.bar }} />
                    </div>
                    <div className="hm-tile-tests">{cat.passed}/{cat.total}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
